import { useFormik } from 'formik';
import {
    CreateLeaveRequestDto,
    LeaveDurationTypeEnum,
    LeaveRequestDto,
    LeaveRequestStatus,
    LeaveRequestType,
} from '../../../commonTypes/leaveRequest.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
    useHttpCreateMedicalLeaveRequest,
    useHttpGetDisabledDays,
    useHttpGetHolidaysByBranchOnMount,
    useHttpSubmitLeaveRequest,
    useOrganizationId,
    useUserOrganizationPolicies,
} from '../../../hooks';
import { AuthState } from '../../../store/slices/auth.slice';
import REQUEST_SCHEMA from './requestSchema';
import { useEffect, useMemo, useState } from 'react';
import DataRangePicker from '../../DateRangePicker';
import { Button, Dropdown, MixerLoader, TextAreaFormik, MediaWidth, IMenuItem, Img, IconButton, Modal, YesNo } from '../../Common';
import { IDateRange, diffDaysWithoutWeekendsAndHolidays } from '../../../util/date/dateHelper';
import { DateTime } from 'luxon';
import { UserOrganizationPolicyDto } from '../../../commonTypes/acs/user.types';
import cc from 'classcat';
import { HolidayDto } from '../../../commonTypes/organization.types';
import { useTranslation } from '../../../hooks/useTranslation';
import useHttpGetPolicyByIdOnMount from '../../../hooks/organization/organizationApi/useHttpGetPolicyByIdOnMount';
import { calculateNewAmountsBySubtracting } from '../../../api/utils/leave';
import useHttpGetMedicalLeaveByOrganizationOnMount from '../../../hooks/organization/organizationApi/useHttpGetMedicalLeaveByOrganizationOnMount';
import { useParams } from 'react-router';
import { MedicalLeaveRequestBaseResponseDto } from '../../../commonTypes/user.types';

export interface IRequestLeaveState {
    leaveType: IMenuItem;
    dates: IDateRange;
    description: string;
    leaveDescriptionRequired: boolean;
    leaveDurationType: LeaveDurationTypeEnum;
}

interface IValidTo {
    date: DateTime;
    output: string;
}

interface IRequestLeave {
    close: () => void;
    createLeaveRequestOnSuccess: (request: LeaveRequestDto[], holidays: HolidayDto[]) => void;
    predefinedDates?: IDateRange;
    isMedicalLeaveRequest?: boolean;
    createMedicalLeaveRequestOnSuccess?: (request: MedicalLeaveRequestBaseResponseDto, holidays: HolidayDto[]) => void;
    heading?: string;
}

type MedicalLeavePolicySummary = {
    leaveType: string;
    autoApproved: boolean;
    reqDescriptionRequired: boolean;
    documentRequired: boolean;
    isActive: boolean;
};

const generateLabelPolicyAndMedicalLeave = (policy: MedicalLeavePolicySummary, autoApprovedLabel: string) => {
    let label = policy?.leaveType;
    if (policy?.autoApproved) label += ` (${autoApprovedLabel})`;
    return label;
};

const parseValidTo = (policy: UserOrganizationPolicyDto): IValidTo => {
    const date = DateTime.fromObject({ day: policy.carriedOverValidToDay, month: policy.carriedOverValidToMonth, year: DateTime.now().year });
    return { date, output: `${policy.carriedOverValidToDay}. ${policy.carriedOverValidToMonth}. ${DateTime.now().year}.` };
};

const RequestLeave = ({
    close,
    createLeaveRequestOnSuccess: leaveRequestOnSuccess,
    createMedicalLeaveRequestOnSuccess: medicalLeaveRequestOnSuccess,
    predefinedDates,
    heading,
    isMedicalLeaveRequest,
}: IRequestLeave) => {
    const { t } = useTranslation();
    const { userSub } = useParams();
    const organizationId = useOrganizationId();
    const { medicalLeave } = useHttpGetMedicalLeaveByOrganizationOnMount(organizationId);
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const policies = useUserOrganizationPolicies();
    const [policiesAndMedicalLeave, setPoliciesAndMedicalLeave] = useState<MedicalLeavePolicySummary[]>([]);

    useEffect(() => {
        if (policies && medicalLeave) {
            const mappedPolicies: MedicalLeavePolicySummary[] = policies.map((p: UserOrganizationPolicyDto) => ({
                leaveType: p.leaveType,
                autoApproved: p.autoApproved,
                reqDescriptionRequired: p.leaveDescriptionRequired,
                documentRequired: false,
                isActive: p.isActive,
            }));

            const mappedMedicalLeave: MedicalLeavePolicySummary = {
                leaveType: medicalLeave.leaveType,
                autoApproved: false,
                reqDescriptionRequired: medicalLeave.reqDescriptionRequired,
                documentRequired: medicalLeave.documentRequired,
                isActive: true,
            };

            setPoliciesAndMedicalLeave([...mappedPolicies, mappedMedicalLeave]);
        }
    }, [policies, medicalLeave]);

    const { data: holidays, isReady: holidaysAreReady } = useHttpGetHolidaysByBranchOnMount(auth.value.branchId);
    const [files, setFiles] = useState<File[]>([]);
    const [isMedicalLeaveCreateModalVisible, setIsMedicalLeaveCreateModalVisible] = useState<boolean>(false);

    const { disabledDays, getDisabledDaysWithMessage, leaveRequestDates } = useHttpGetDisabledDays();

    const fetchDisabledDays = (from: string, to: string) => {
        void getDisabledDaysWithMessage(from, to);
    };

    const { createLeaveRequest } = useHttpSubmitLeaveRequest();
    const { createMedicalLeaveRequest } = useHttpCreateMedicalLeaveRequest();

    const requestLeave = (values: IRequestLeaveState) => {
        if (values.dates.startDate) {
            let endDate = values.dates.endDate;
            if (!endDate) endDate = values.dates.startDate;

            let body: CreateLeaveRequestDto = {
                fromDate: values.dates.startDate.toISO(),
                toDate: endDate.toISO(),
                type: values.leaveType.value.toString(),
                description: values.description,
                leaveDurationType: values.leaveDurationType,
            };

            void createLeaveRequest(body, { onSuccess: res => leaveRequestOnSuccess(res, holidays) });
            close();
        }
    };

    const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles) return;

        const newFiles = Array.from(selectedFiles);
        setFiles(prev => {
            const existingFileNames = new Set(prev.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(f => !existingFileNames.has(f.name));
            return [...prev, ...uniqueNewFiles];
        });

        event.target.value = '';
    };

    const requestMedicalLeave = async (values: IRequestLeaveState) => {
        if (values.dates.startDate) {
            try {
                let body = {
                    startDate: values.dates.startDate.toISODate(),
                    endDate: values.dates.endDate ? values.dates.endDate.toISODate() : undefined,
                    description: values.description || undefined,
                    files: files,
                    status: LeaveRequestStatus.PENDING,
                    userSub: isMedicalLeaveRequest ? userSub ?? '' : auth.value.userSub,
                };

                void createMedicalLeaveRequest(body, {
                    onSuccess: res => {
                        medicalLeaveRequestOnSuccess && medicalLeaveRequestOnSuccess(res, holidays);
                        close();
                        window.location.reload();
                    },
                });
            } catch (error) {
                console.error('Error creating medical leave', error);
            }
        }
    };

    const generateTotalNumberOfDays = (fromDate: DateTime | null, toDate: DateTime | null, durationType: LeaveDurationTypeEnum) => {
        let days = 0;
        if (fromDate && !toDate) {
            days = 1;
        } else if (fromDate && toDate) {
            days = diffDaysWithoutWeekendsAndHolidays(fromDate, toDate, holidays, leaveRequestDates);
        }

        const isHalfDay = durationType === LeaveDurationTypeEnum.FIRST_DAY_HALF || durationType === LeaveDurationTypeEnum.SECOND_DAY_HALF;
        return isHalfDay ? days / 2 : days;
    };

    const vacationIndex = useMemo(() => {
        const index = policies.findIndex(policy => policy.leaveType === LeaveRequestType.VACATION);
        return index !== -1 ? index : 0;
    }, [policies]);

    const INITIAL_REQUEST_STATE: IRequestLeaveState = {
        leaveType:
            isMedicalLeaveRequest && medicalLeave
                ? { label: 'Medical Leave', value: LeaveRequestType.MEDICAL_LEAVE }
                : {
                      label: generateLabelPolicyAndMedicalLeave(policiesAndMedicalLeave[vacationIndex], t('leaveRequest.autoApproved')),
                      value: policies[vacationIndex]?.leaveType,
                  },
        dates: {
            startDate: predefinedDates?.startDate ?? null,
            endDate: null,
        },
        description: '',
        leaveDescriptionRequired: policies[vacationIndex]?.leaveDescriptionRequired ?? false,
        leaveDurationType: LeaveDurationTypeEnum.FULL_DAY,
    };

    const context = useFormik<IRequestLeaveState>({
        initialValues: INITIAL_REQUEST_STATE,
        enableReinitialize: true,
        validationSchema: REQUEST_SCHEMA(t),
        onSubmit: v => {
            if (v.leaveType?.value === LeaveRequestType.MEDICAL_LEAVE) {
                setIsMedicalLeaveCreateModalVisible(true);
            } else {
                requestLeave(v);
            }
        },
    });

    const selectedPolicy = policies.find(policy => policy.leaveType === context.values.leaveType.value) ?? policies[vacationIndex];
    const fromDate = context.values.dates.startDate;
    const toDate = context.values.dates.endDate;
    const selectedPolicyAndMedicalLeave = policiesAndMedicalLeave.find((p: any) => p.leaveType === context.values.leaveType.value);

    const totalNumberOfDays = generateTotalNumberOfDays(fromDate, toDate, context.values.leaveDurationType);

    const { newAmount, newCarriedOverAmount, newMaximumNegativeAmount } =
        fromDate && selectedPolicy
            ? calculateNewAmountsBySubtracting(
                  fromDate,
                  toDate ?? fromDate,
                  selectedPolicy,
                  context.values.leaveDurationType,
                  holidays,
                  leaveRequestDates
              )
            : {
                  newAmount: selectedPolicy?.amount ?? 0,
                  newCarriedOverAmount: selectedPolicy?.carriedOverAmount ?? 0,
                  newMaximumNegativeAmount: selectedPolicy?.maximumNegativeAmount ?? 0,
              };

    const onPolicyChange = (item: IMenuItem) => {
        context.setFieldValue('leaveType', item).catch(() => {});
        const policy = policies.find(policy => policy.leaveType === item.value);
        context.setFieldValue('leaveDescriptionRequired', policy?.leaveDescriptionRequired).catch(() => {});
    };

    const handleShowDocument = (file: File) => {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 100);
    };

    const handleDownloadDocument = (file: File) => {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 100);
    };

    const checkIfButtonIsDisabled = () => {
        const { startDate, endDate } = context.values.dates;

        if (!startDate) return true;
        if (Object.keys(context.errors).length > 0) return true;

        const end = endDate ?? startDate;
        const selectedInterval = DateTime.fromISO(startDate.toISO()).until(DateTime.fromISO(end.toISO()));

        const allDisabledDates = disabledDays
            .filter(d => d.message !== t('leaveRequest.youHaveLeaveRequest'))
            .flatMap(d => d.dates.map(date => DateTime.fromISO(date)));

        const overlaps = selectedInterval.splitBy({ day: 1 }).some(d => allDisabledDates.some(dd => dd.hasSame(d.start, 'day')));

        if (overlaps) return true;

        if (context.values.leaveType?.value === LeaveRequestType.MEDICAL_LEAVE) {
            const descriptionRequired = selectedPolicyAndMedicalLeave?.reqDescriptionRequired;
            const documentRequired = selectedPolicyAndMedicalLeave?.documentRequired;

            const isDescriptionEmpty = descriptionRequired && !context.values.description?.trim();
            const areDocumentsMissing = documentRequired && files.length === 0;

            return isDescriptionEmpty || areDocumentsMissing;
        }

        return false;
    };

    const organizationPolicy = useHttpGetPolicyByIdOnMount(selectedPolicy?.organizationPolicyId ?? '');
    const defaultHeading = heading ?? t('leaveRequest.requestButton');

    return (
        <div className="request-leave-n">
            <h3 className="request-leave-n__header">{defaultHeading}</h3>
            <form className="request-leave-n__form" onSubmit={context.handleSubmit}>
                <div className="request-leave-n__inputs">
                    <div className="request-leave-n__main">
                        <Dropdown
                            label="Policy"
                            menuItems={policiesAndMedicalLeave
                                .filter(policy => policy.isActive)
                                .map((policy: MedicalLeavePolicySummary) => ({
                                    value: policy.leaveType,
                                    label: generateLabelPolicyAndMedicalLeave(policy, t('leaveRequest.autoApproved')),
                                }))}
                            selected={context.values.leaveType}
                            onChange={item => onPolicyChange(item)}
                            testId="policy-dropdown"
                            disabled={isMedicalLeaveRequest}
                        />

                        {selectedPolicyAndMedicalLeave?.leaveType === LeaveRequestType.MEDICAL_LEAVE ? (
                            <div>
                                <TextAreaFormik
                                    id="description"
                                    label={`Description`}
                                    placeholder="Add description"
                                    isRequired={selectedPolicyAndMedicalLeave?.reqDescriptionRequired ?? false}
                                    context={context}
                                    testId="test-description"
                                />

                                <label className="text-area__label paragraph-4">
                                    Attach documents
                                    {selectedPolicyAndMedicalLeave?.documentRequired && <span style={{ color: 'red' }}>*</span>}
                                </label>
                                <div className="request-leave-n__documents__upload-wrapper">
                                    <label htmlFor="documents-upload">
                                        <div>
                                            <IconButton
                                                iconUrl={'/svg/document-upload.svg'}
                                                iconAlt="upload-document"
                                                variant="outline"
                                                onClick={() => document.getElementById('documents-upload')?.click()}
                                                type="button"
                                            />
                                        </div>
                                        <input
                                            id="documents-upload"
                                            type="file"
                                            accept=".pdf"
                                            multiple
                                            onChange={handleFilesChange}
                                            style={{ display: 'none' }}
                                            required={selectedPolicyAndMedicalLeave?.documentRequired}
                                        />
                                    </label>

                                    <div className="request-leave-n__documents__selected-files">
                                        {files.map((file, index) => (
                                            <div key={index} className="request-leave-n__documents__file-item">
                                                <span className="request-leave-n__documents__file-name paragraph-4">{file.name}</span>

                                                <div className="request-leave-n__documents__upload-wrapper">
                                                    <Img
                                                        src="/svg/eye.svg"
                                                        alt="Preview"
                                                        className="request-leave-n__documents__file-action-icon"
                                                        onClick={() => handleShowDocument(file)}
                                                    />
                                                    <Img
                                                        src="/svg/document-download.svg"
                                                        alt="Download"
                                                        className="request-leave-n__documents__file-action-icon"
                                                        onClick={() => handleDownloadDocument(file)}
                                                    />
                                                    <Img
                                                        src="/svg/close-x-blue.svg"
                                                        alt="Delete"
                                                        className="request-leave-n__documents__file-action-icon"
                                                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== index))}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="request-leave-n__main">
                                {organizationPolicy.policy?.shortestLeaveDuration === LeaveDurationTypeEnum.HALF_DAY && (
                                    <Dropdown
                                        label="Type"
                                        menuItems={[
                                            { value: LeaveDurationTypeEnum.FULL_DAY, label: t('leaveRequest.fullDay') },
                                            { value: LeaveDurationTypeEnum.FIRST_DAY_HALF, label: t('leaveRequest.firstDayHalf') },
                                            { value: LeaveDurationTypeEnum.SECOND_DAY_HALF, label: t('leaveRequest.secondDayHalf') },
                                        ]}
                                        selected={{
                                            value: context.values.leaveDurationType || LeaveDurationTypeEnum.FULL_DAY,
                                            label:
                                                context.values.leaveDurationType === LeaveDurationTypeEnum.FIRST_DAY_HALF
                                                    ? t('leaveRequest.firstDayHalf')
                                                    : context.values.leaveDurationType === LeaveDurationTypeEnum.SECOND_DAY_HALF
                                                    ? t('leaveRequest.secondDayHalf')
                                                    : t('leaveRequest.fullDay'),
                                        }}
                                        onChange={item => context.setFieldValue('leaveDurationType', item.value)}
                                        testId="leave-duration-dropdown"
                                    />
                                )}
                                <TextAreaFormik
                                    id="description"
                                    label={`Description`}
                                    placeholder="Add description"
                                    isRequired={selectedPolicy?.leaveDescriptionRequired ?? false}
                                    context={context}
                                    testId="test-description"
                                />
                                <MediaWidth min="lg">
                                    {selectedPolicy && (
                                        <RequestLeaveTotal
                                            newAmount={newAmount}
                                            newCarriedOverAmount={newCarriedOverAmount}
                                            newMaximumNegativeAmount={newMaximumNegativeAmount}
                                            totalNumberOfDays={totalNumberOfDays}
                                            selectedPolicy={selectedPolicy}
                                            validTo={parseValidTo(selectedPolicy)}
                                        />
                                    )}
                                </MediaWidth>
                            </div>
                        )}
                    </div>

                    {!holidaysAreReady ? (
                        <MixerLoader position="centerOnParent" size="medium" />
                    ) : (
                        <DataRangePicker
                            values={context.values}
                            disabledDays={disabledDays}
                            setFieldValue={(field, value) => {
                                if (field === 'dates') {
                                    context.setFieldValue('dates', value).catch(() => {});
                                } else {
                                    context.setFieldValue(field, value).catch(() => {});
                                }
                            }}
                            onPeriodChange={fetchDisabledDays}
                            holidays={holidays}
                            initialDate={predefinedDates?.startDate}
                        />
                    )}

                    <MediaWidth max="lg">
                        {selectedPolicy && (
                            <RequestLeaveTotal
                                newAmount={newAmount}
                                newCarriedOverAmount={newCarriedOverAmount}
                                newMaximumNegativeAmount={newMaximumNegativeAmount}
                                totalNumberOfDays={totalNumberOfDays}
                                selectedPolicy={selectedPolicy}
                                validTo={parseValidTo(selectedPolicy)}
                            />
                        )}
                    </MediaWidth>
                </div>
                <div className="request-leave-n__buttons">
                    <Button text="Cancel" variant="text" onClick={close} color="blue" />
                    <Button isSubmit text={t('leaveRequest.requestButton')} disabled={checkIfButtonIsDisabled()} testId="submit-button" />
                </div>
            </form>
            <Modal isVisible={isMedicalLeaveCreateModalVisible} closeModal={() => setIsMedicalLeaveCreateModalVisible(false)}>
                <YesNo
                    title={t('medicalLeaveRequest.create')}
                    description={t('medicalLeaveRequest.createDescription')}
                    onYes={() => {
                        requestMedicalLeave(context.values).catch(() => {});
                        setIsMedicalLeaveCreateModalVisible(false);
                    }}
                    onNo={() => setIsMedicalLeaveCreateModalVisible(false)}
                />
            </Modal>
        </div>
    );
};

interface IRequestLeaveTotal {
    newAmount: number;
    newCarriedOverAmount: number;
    newMaximumNegativeAmount: number;
    totalNumberOfDays: number;
    selectedPolicy: UserOrganizationPolicyDto;
    validTo: IValidTo;
}

const RequestLeaveTotal = ({
    newAmount,
    newCarriedOverAmount,
    newMaximumNegativeAmount,
    selectedPolicy,
    totalNumberOfDays,
    validTo,
}: IRequestLeaveTotal) => {
    const { t } = useTranslation('leaveRequest');

    return (
        <div className="request-leave-n__sums">
            <p className="paragraph-3 request-leave-n__total">
                {t('annualDays')}:{' '}
                <span className={cc(['request-leave-n__value', { 'request-leave-n__value--error': newAmount < 0 }])}>{`${newAmount}${t(
                    'dayUnit'
                )}`}</span>
            </p>
            {selectedPolicy.isCarriedOver && DateTime.now() <= validTo.date && (
                <div>
                    <p className="paragraph-3 request-leave-n__total">
                        {t('carriedOverDays')}: <span className="request-leave-n__value">{`${newCarriedOverAmount}${t('dayUnit')}`}</span>
                    </p>
                    <p className="paragraph-4 request-leave-n__total">{`(${t('validTo')}: ${validTo.output})`}</p>
                </div>
            )}
            {selectedPolicy.maximumNegativeAmount > 0 && (
                <div>
                    <p className="paragraph-3 request-leave-n__total">
                        {t('maximumNegativeAmount')}
                        <span className="request-leave-n__value">{`${newMaximumNegativeAmount}${t('dayUnit')}`}</span>
                    </p>
                </div>
            )}
            <p className="paragraph-3 request-leave-n__total">
                {t('total')}:{' '}
                {totalNumberOfDays > 0 ? (
                    <span className="request-leave-n__value">{`${totalNumberOfDays}${t('dayUnit')}`}</span>
                ) : (
                    <span>{t('daysNotSelected')}</span>
                )}
            </p>
        </div>
    );
};

export default RequestLeave;
